import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { licensingApi } from '@/api/licensing';
import { Button, Modal, Input } from '@/components/ui';

type Status = 'active' | 'trial' | 'suspended' | 'cancelled';

interface Sub {
  id: string;
  system_code: string;
  plan_code: string;
  status: string;
  effective_price_agorot?: number | null;
  override_reason?: string | null;
  coupon_code?: string | null;
  expires_at: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  orgId: string;
  sub: Sub;
}

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export default function SubscriptionEditModal({ open, onClose, orgId, sub }: Props) {
  const qc = useQueryClient();

  const plansQ = useQuery({
    queryKey: ['license-plans', sub.system_code],
    queryFn: () => licensingApi.listPlans(sub.system_code),
    enabled: open,
  });

  const [planCode, setPlanCode] = useState(sub.plan_code);
  const [status, setStatus] = useState<Status>((sub.status as Status) ?? 'active');
  const [priceNis, setPriceNis] = useState<string>('');
  const [overrideReason, setOverrideReason] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');

  const initialPriceNis = useMemo(() => {
    const a = sub.effective_price_agorot;
    return a == null ? '' : String((Number(a) / 100).toFixed(2));
  }, [sub.effective_price_agorot]);

  useEffect(() => {
    if (!open) return;
    setPlanCode(sub.plan_code);
    setStatus((sub.status as Status) ?? 'active');
    setPriceNis(initialPriceNis);
    setOverrideReason(sub.override_reason ?? '');
    setExpiresAt(toDateInput(sub.expires_at));
  }, [open, sub, initialPriceNis]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const priceAgorot = priceNis.trim() === '' ? null : Math.round(Number(priceNis) * 100);
      if (priceAgorot != null && (!Number.isFinite(priceAgorot) || priceAgorot < 0)) {
        throw new Error('Invalid effective price');
      }
      await licensingApi.updateSubscriptionFields(sub.id, {
        planCode,
        status,
        effectivePriceAgorot: priceAgorot,
        overrideReason: overrideReason.trim() || null,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
    },
    onSuccess: async () => {
      toast.success('Subscription updated');
      await qc.invalidateQueries({ queryKey: ['admin-org-subs', orgId] });
      await qc.invalidateQueries({ queryKey: ['admin-org-effective-limits', orgId] });
      await qc.invalidateQueries({ queryKey: ['org-effective-limits', orgId] });
      await qc.invalidateQueries({ queryKey: ['admin-org-jumpstart-sub', orgId] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.message ?? 'Update failed'),
  });

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Edit subscription"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saveMut.isPending}>
            Cancel
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-ink-secondary mb-1">Plan</label>
          <select
            value={planCode}
            onChange={(e) => setPlanCode(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm text-white"
            style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
          >
            {(plansQ.data ?? []).map((p) => (
              <option key={p.code} value={p.code}>
                {p.name} ({p.code})
              </option>
            ))}
            {!(plansQ.data ?? []).some((p) => p.code === planCode) && (
              <option value={planCode}>{planCode} (current)</option>
            )}
          </select>
        </div>

        <div>
          <label className="block text-xs text-ink-secondary mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as Status)}
            className="w-full px-3 py-2 rounded-lg text-sm text-white"
            style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
          >
            <option value="active">active</option>
            <option value="trial">trial</option>
            <option value="suspended">suspended</option>
            <option value="cancelled">cancelled</option>
          </select>
        </div>

        <Input
          label="Effective price ₪/mo (+VAT / net)"
          type="number"
          min="0"
          step="0.01"
          value={priceNis}
          onChange={(e) => setPriceNis(e.target.value)}
          placeholder="leave blank to keep current — never VAT-inclusive"
        />

        <Input
          label="Override reason"
          value={overrideReason}
          onChange={(e) => setOverrideReason(e.target.value)}
        />

        <div>
          <label className="block text-xs text-ink-secondary mb-1">Coupon code</label>
          <div
            className="px-3 py-2 rounded-lg text-sm text-ink-secondary"
            style={{ background: '#0f1117', border: '1px solid #2a2d3e' }}
          >
            {sub.coupon_code ?? '—'}
          </div>
        </div>

        <Input
          label="Expires at"
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
        />

        {saveMut.error ? (
          <p className="text-xs text-red-400">{(saveMut.error as Error).message}</p>
        ) : null}
      </div>
    </Modal>
  );
}
